import { useCallback, useState } from "react";

import type { PDFAnnot } from "@alaffia-technology-solutions/pdf-viewer";

import {
  usePDF,
  usePDFTextSelection,
  usePDFViewerEvent,
} from "@alaffia-technology-solutions/pdf-viewer";

interface UsePDFViewerProps {
  onSearch?: (query: string) => void;
  searchQuery: string;
}

export const usePDFViewer = ({
  onSearch: extOnSearch,
  searchQuery,
}: UsePDFViewerProps) => {
  const {
    addHighlight,
    bookmarks,
    currentPage,
    error,
    events,
    getPageDimensions,
    getPageThumbnail,
    highlightTextOnPage,
    jumpToPage,
    nextPage,
    pagesCount,
    prevPage,
    readOnlyScale,
    removeHighlights,
    rotate,
    setViewMode,
    state,
    viewMode,
    zoom,
  } = usePDF();

  const isDisabled = Boolean(error) || state !== "opened";

  const { textSelection, clearTextSelection, copyTextSelection } =
    usePDFTextSelection();

  const [highlightsMap, setHighlightsMap] = useState<Map<number, PDFAnnot[]>>(
    new Map(),
  );

  const highlight = useCallback(
    async (pageIndex: number, query: string) => {
      if (query) {
        const getPageIndexesToHighlight = (pageIndex: number) => {
          const startIndex = Math.max(0, pageIndex - 3);
          return Array.from({ length: 7 }, (_, i) => startIndex + i);
        };

        const pageIndexesToHighlight = getPageIndexesToHighlight(pageIndex);

        for (const pageIndexToHighlight of pageIndexesToHighlight) {
          if (!highlightsMap.has(pageIndexToHighlight)) {
            const pageHighlights =
              (await highlightTextOnPage(pageIndexToHighlight, [query])) ?? [];

            if (pageHighlights.length) {
              setHighlightsMap(
                highlightsMap.set(pageIndexToHighlight, pageHighlights),
              );
            }
          }
        }
      }
    },
    [highlightsMap, highlightTextOnPage],
  );

  const onSearch = useCallback(
    async (query: string) => {
      extOnSearch?.(query);
      const highlightsToRemove = Array.from(highlightsMap.values()).flat();
      await removeHighlights(highlightsToRemove);
      setHighlightsMap(new Map());
      await highlight(currentPage - 1, query);
    },
    [currentPage, extOnSearch, highlightsMap, highlight, removeHighlights],
  );

  usePDFViewerEvent(
    events?.pageNumberChange,
    (page: number) => void highlight(page - 1, searchQuery),
  );

  return {
    addHighlight,
    bookmarks,
    currentPage,
    events,
    getPageDimensions,
    getPageThumbnail,
    highlightTextOnPage,
    isDisabled,
    jumpToPage,
    nextPage,
    onSearch,
    pagesCount,
    prevPage,
    readOnlyScale,
    removeHighlights,
    rotate,
    setViewMode,
    state,
    viewMode,
    zoom,
    textSelection,
    clearTextSelection,
    copyTextSelection,
  };
};
